import React, { FormEvent, useCallback, useRef, useState } from "react";

import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { Button, Text, Stack, TextInput, Title, Modal } from "@mantine/core";
import { string as yupString } from "yup";

import { MAILCHIMP_FORM } from "config";
import { ReactComponent as CheckIcon } from "~/assets/icons/check-green.svg";

const GetUpdatesModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [requestSent, setRequestSent] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>("");

  const emailSchema = yupString()
    .required("Email is required")
    .email("Invalid email")
    .trim();

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      try {
        event.preventDefault();
        setLoading(true);
        setError("");
        if (!emailRef?.current) return false;

        const email = await emailSchema.validate(emailRef?.current?.value);
        emailRef.current.value = email;

        const form = event.target as HTMLFormElement;
        await fetch(form.action, {
          method: form.method,
          mode: "no-cors",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          body: new URLSearchParams(new FormData(form) as any),
        });

        setRequestSent(true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setError(error?.message ?? "Error");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [emailRef, setRequestSent, setError, setLoading]
  );

  if (requestSent) {
    return (
      <Modal size="lg" opened={open} withCloseButton={false} onClose={onClose}>
        <Stack mt="md" spacing="xs" align="center">
          <CheckIcon height={32} width={32} />
          <Title order={5}>Thanks for signing up</Title>
          <ModalSection>
            <Button fullWidth size="xl" onClick={onClose}>
              Done
            </Button>
          </ModalSection>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal
      size="lg"
      opened={open}
      onClose={onClose}
      title={
        <Text size="lg" weight={500}>
          Get updates
        </Text>
      }
    >
      <Stack spacing="xs">
        <Text size="sm">
          Sign up to receive product updates, news, and more.
        </Text>
        <div id="mc_embed_signup">
          <form
            action={MAILCHIMP_FORM.url}
            method="post"
            id="mc-embedded-subscribe-form"
            name="mc-embedded-subscribe-form"
            target="_self"
            onSubmit={handleSubmit}
          >
            <div id="mc_embed_signup_scroll">
              <TextInput
                name="EMAIL"
                id="mce-EMAIL"
                ref={emailRef}
                placeholder="Email address"
                onChange={() => setError("")}
                error={error}
              />
              <div
                css={{ position: "absolute", left: "-5000px" }}
                aria-hidden="true"
              >
                <TextInput
                  type="text"
                  name={MAILCHIMP_FORM.validation}
                  defaultValue=""
                />
              </div>
              <ModalSection>
                <Button
                  fullWidth
                  size="xl"
                  type="submit"
                  name="subscribe"
                  id="mc-embedded-subscribe"
                  loading={loading}
                >
                  Sign up
                </Button>
              </ModalSection>
            </div>
          </form>
        </div>
      </Stack>
    </Modal>
  );
};

export default GetUpdatesModal;
